import { BigNumberSchema, ConditionSchema, type Serializable, SerializableSchema, z } from "@repo/utils";
import { EntityIdSchema } from "@repo/core/universal";
import { AttachmentIdSchema, InboxIdSchema, MessageIdSchema, ThreadIdSchema } from "../inbox/z.js";
import {
    IntegrationAuthSchema,
    IntegrationConnectionSchema,
    MasterDataQuerySchema,
    PurchaseOrderExtraCalculationSchema,
    PurchaseOrderExtraTypeSchema,
    PurchaseOrderUpdateSchema,
} from "../erp/z.js";
import { LanguageSchema, OrganizationIdSchema } from "../organization/z.js";
import { OrderConfirmationProcessingResultSchema, OrderConfirmationsExtractionResultSchema } from "./workflows/po/z.js";

export const WorkflowResultIdSchema = EntityIdSchema("wfr");
export const WorkflowResultSchema = z.object({ resultId: WorkflowResultIdSchema });

export const ApprovalTaskIdSchema = EntityIdSchema("apt");
export const ApprovalDecisionIdSchema = EntityIdSchema("apd");
export const AssistantCaseIdSchema = EntityIdSchema("acs");
export const AssistantCaseFileIdSchema = EntityIdSchema("csf");
export const AssistantCaseAcknowledgementIdSchema = EntityIdSchema("csa");
export const AssistantCaseReviewIdSchema = EntityIdSchema("csr");
export const ActivityPayloadIdSchema = EntityIdSchema("pld");

export const MoneySchema = z.object({
    amount: z.nullable(BigNumberSchema),
    currency: z.nullable(z.string()),
});
export const QuantitySchema = z.object({
    amount: z.nullable(BigNumberSchema),
    unit: z.nullable(z.string()),
});

export const ExtraLineSchema = z.object({
    type: PurchaseOrderExtraTypeSchema,
    calculation: PurchaseOrderExtraCalculationSchema,
    name: z.optional(z.string()),
});

export const PriceSchema = z.object({
    amount: z.nullable(MoneySchema),
    quantity: z.nullable(QuantitySchema),
    extra: z.optional(z.array(ExtraLineSchema)),
});
export const POStatusSchema = z.enum(["open", "partiallyConfirmed", "confirmed"]);

export const ToolCallErrorSchema = z.object({ name: z.string(), message: z.string() });

export function WorkflowOptionsSchema<T extends z.ZodMiniType>(argsSchema: T) {
    return z.object({
        args: z.array(argsSchema),
        workflowId: z.optional(z.string()),
        taskQueue: z._default(z.string(), "experiments"),
        workflowIdConflictPolicy: z._default(
            z.enum(["FAIL", "USE_EXISTING", "TERMINATE_EXISTING"]),
            "TERMINATE_EXISTING",
        ),
        organizationId: z.optional(OrganizationIdSchema),
    });
}

export function ChildWorkflowOptionsSchema<T extends z.ZodMiniType<Serializable>>(argsSchema: T) {
    return z.object({
        args: z.array(argsSchema),
        workflowId: z.string(),
        taskQueue: z._default(z.string(), "experiments"),
        organizationId: z.optional(OrganizationIdSchema),
    });
}

export const PipelineTypeSchema = z.enum(["so", "po", "doc", "brose"]);

export const WorkflowValidationErrorSchema = z.object({
    path: z.string(),
    error: z.string(),
});

const assistantFieldValueSchemas = {
    string: z.nullable(z.string()),
    boolean: z.nullable(z.boolean()),
    date: z.nullable(z.string()),
    price: PriceSchema,
    money: MoneySchema,
    quantity: QuantitySchema,
    poStatus: z.nullable(POStatusSchema),
    registry: z.object({ id: z.nullable(z.string()), source: z.enum(["paymentTerms"]) }),
    deliveryTerms: z.object({
        incoterms1: z.nullable(z.string()),
        incoterms2: z.nullable(z.string()),
    }),
    scheduleLines: z.object({
        schedules: z.array(
            z.object({
                quantity: QuantitySchema,
                deliveryDate: z.nullable(z.string()),
                reference: z.nullable(z.string()),
                status: z.optional(z.string()),
            }),
        ),
        combineMode: z._default(z.enum(["partial", "reduction"]), "partial"),
        confirmations: z.array(
            z.object({
                quantity: QuantitySchema,
                deliveryDate: z.nullable(z.string()),
                reference: z.nullable(z.string()),
            }),
        ),
    }),
    extra: z.object({
        lines: z.array(ExtraLineSchema),
    }),
};

export const AssistantFieldValueTypeSchema = z.enum(
    Object.keys(assistantFieldValueSchemas) as (keyof typeof assistantFieldValueSchemas)[],
);
type AssistantFieldValueType = z.infer<typeof AssistantFieldValueTypeSchema>;

export type AssistantFieldValueMap = {
    [P in AssistantFieldValueType]: {
        type: P;
        value: z.infer<(typeof assistantFieldValueSchemas)[P]>;
    };
};

export const AssistantFieldValueSchema = z
    .union(
        Array.from(Object.entries(assistantFieldValueSchemas)).map(([k, v]) =>
            z.object({
                type: z.literal(k as AssistantFieldValueType),
                value: v,
            } as {
                [P in AssistantFieldValueType]: {
                    type: z.ZodMiniLiteral<P>;
                    value: (typeof assistantFieldValueSchemas)[P];
                };
            }[AssistantFieldValueType]),
        ),
    )
    .register(z.globalRegistry, { title: "AssistantFieldValueSchema" });

export const AssistantFieldValueValueSchema = z.union(Array.from(Object.values(assistantFieldValueSchemas)));

export const AssistantCorrectionsSchema = z.record(z.string(), AssistantFieldValueValueSchema);

export const AssistantFlagReasonSchema = z.enum(["turianError", "partialConfirmation"]);

export const AssistantDecisionApprovalSchema = z.object({
    state: z.enum(["no", "pending", "approved", "declined"]),
    comment: z.optional(z.string()),
    includeInEmail: z.optional(z.boolean()),
    origin: z.string(),
});

export const AssistantDecisionSchema = z
    .discriminatedUnion("type", [
        z.object({
            type: z.enum(["accepted", "edited"]),
            approval: AssistantDecisionApprovalSchema,
            value: AssistantFieldValueSchema,
            origin: z.string(),
        }),
        z.object({
            type: z.literal("rejected"),
            value: AssistantFieldValueSchema,
            includeInEmail: z.optional(z.boolean()),
            comment: z.optional(z.string()),
            origin: z.string(),
        }),
        z.object({
            type: z.literal("flagged"),
            value: z.optional(AssistantFieldValueSchema),
            reason: z.optional(AssistantFlagReasonSchema),
            origin: z.string(),
        }),
        z.object({
            type: z.literal("ignored"),
            value: z.optional(AssistantFieldValueSchema),
            origin: z.string(),
        }),
    ])
    .register(z.globalRegistry, { title: "AssistantDecisionSchema" });

export const AssistantDecisionsSchema = z.record(z.string(), AssistantDecisionSchema);

export const AssistantMasterPriceDecisionsSchema = z.record(
    z.string(),
    z.discriminatedUnion("type", [
        z.object({
            type: z.literal("accepted"),
            value: z.nullable(BigNumberSchema),
            id: z.optional(z.string()),
        }),
        z.object({
            type: z.literal("rejected"),
        }),
    ]),
);

export const ApproverRoleSchema = z.union([z.string(), z.object({ email: z.union([z.literal("self"), z.string()]) })]);

export const ThresholdDirectionSchema = z.enum(["positive", "negative", "both"]);
export const ThresholdNumericFieldSchema = z.enum([
    "unitPrice",
    "quantity",
    "scheduleLinesQuantity",
    "orderTotalPrice",
]);
export const ThresholdComparableFieldSchema = z.enum([
    ...Object.values(ThresholdNumericFieldSchema.def.entries),
    "deliveryDate",
]);
export const ThresholdFieldSchema = z.enum([
    ...Object.values(ThresholdComparableFieldSchema.def.entries),
    "deliveryTerms",
    "paymentTerms",
    "itemExtra",
    "headerExtra",
]);

export const ThresholdSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("alwaysWithin"),
        field: ThresholdFieldSchema,
        active: z.boolean(),
    }),
    z.object({
        type: z.literal("anyChange"),
        field: ThresholdFieldSchema,
        active: z.boolean(),
    }),
    z.object({
        type: z.literal("absoluteDifference"),
        field: ThresholdComparableFieldSchema,
        direction: ThresholdDirectionSchema,
        value: z.number(),
        active: z.boolean(),
    }),
    z.object({
        type: z.literal("absoluteValue"),
        field: ThresholdNumericFieldSchema,
        direction: ThresholdDirectionSchema,
        value: z.number(),
        active: z.boolean(),
    }),
    z.object({
        type: z.literal("percentage"),
        field: ThresholdNumericFieldSchema,
        direction: ThresholdDirectionSchema,
        value: z.number(),
        active: z.boolean(),
    }),
]);

export const ThresholdTriggerSchema = z.object({
    threshold: ThresholdSchema,
    key: z.string(),
    before: SerializableSchema,
    after: SerializableSchema,
    beyond: z.boolean(),
});

export const ApprovalThresholdSchema = z.intersection(
    z.object({
        role: ApproverRoleSchema,
    }),
    ThresholdSchema,
);

export const TriggeredApprovalThresholdSchema = z.intersection(
    ApprovalThresholdSchema,
    z.object({ key: z.string(), before: SerializableSchema, after: SerializableSchema }),
);

export const ResolvedApproverSchema = z.object({
    key: z.string(),
    entity: SerializableSchema,
    email: z.nullable(z.string()),
    role: ApproverRoleSchema,
});

export const ErpBusinessErrorSchema = z.object({ code: z.string(), message: z.string() });
export const WorkflowFailureSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("NotImplemented"),
        details: z.tuple([]),
    }),
    z.object({
        type: z.literal("ERPRequestError"),
        details: z.tuple([
            z.object({
                first: z.optional(z.boolean()),
                status: z.number(),
                message: z.string(),
            }),
        ]),
    }),
    z.object({
        type: z.literal("ERPReadBusinessError"),
        details: z.tuple([
            z.object({
                errors: z.array(ErpBusinessErrorSchema),
            }),
        ]),
    }),
    z.object({
        type: z.literal("ERPModifyBusinessError"),
        details: z.tuple([
            z.object({
                errors: z.array(ErpBusinessErrorSchema),
                first: z.boolean(),
            }),
        ]),
    }),
    z.object({
        type: z.literal("Unknown"),
        details: z.tuple([
            z.object({
                type: z.nullable(z.string()),
                message: z.nullable(z.string()),
            }),
        ]),
    }),
]);

export const ThreadProcessingErrorSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("noConfirmationExtracted"),
    }),
    z.object({
        type: z.literal("unsupportedFileType"),
    }),
    z.object({
        type: z.literal("workflowFailed"),
        failure: WorkflowFailureSchema,
    }),
]);

export const ThreadProcessingResultSchema = z.union([
    z.object({
        meta: z.object({ traceId: z.string() }),
        extractionResult: OrderConfirmationsExtractionResultSchema,
    }),
    z.object({
        meta: z.object({ traceId: z.string() }),
        error: z.union([z.object({ message: z.string() }), ThreadProcessingErrorSchema]),
    }),
]);

export const ThreadProcessingStatusSchema = z.enum(["pending", "finished", "error", "erp-error"]);

export const ThreadProcessingSchema = z.object({
    id: z.number(),
    organizationId: OrganizationIdSchema,
    threadId: ThreadIdSchema,
    status: ThreadProcessingStatusSchema,
    result: z.nullable(ThreadProcessingResultSchema),
});

export const PurchaseOrderProcessingErrorSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("noPurchaseOrderFound"),
        number: z.string(),
    }),
    z.object({
        type: z.literal("supplierProcessingDisabled"),
    }),
    z.object({
        type: z.literal("workflowFailed"),
        failure: WorkflowFailureSchema,
    }),
]);

export const MasterPriceWhitelistSchema = z.object({
    positions: z.record(
        z.string(),
        z.object({
            updatable: z.boolean(),
            id: z.optional(z.string()),
        }),
    ),
});

export const PurchaseOrderProcessingErrorResultSchema = z.object({
    error: PurchaseOrderProcessingErrorSchema,
});

export const PurchaseOrderPostProcessingStateSchema = z.enum(["review", "approval", "submission", "failure"]);

export const POProcessingResultSchema = z.union([
    z.object({
        result: OrderConfirmationProcessingResultSchema,
        masterPriceWhitelist: z.optional(MasterPriceWhitelistSchema),
        decisions: z.optional(AssistantDecisionsSchema),
        masterPriceDecisions: z.optional(AssistantMasterPriceDecisionsSchema),
        update: z.optional(PurchaseOrderUpdateSchema),
        corrections: z.optional(AssistantCorrectionsSchema),
        triggeredApprovalThresholds: z.optional(z.array(TriggeredApprovalThresholdSchema)),
        triggeredAsOrderedThresholds: z.optional(z.array(ThresholdTriggerSchema)),
        triggeredAutoAcceptThresholds: z.optional(z.array(ThresholdTriggerSchema)),
        resolvedApprovers: z.optional(z.array(ResolvedApproverSchema)),
        failure: z.optional(WorkflowFailureSchema),
        postProcessingState: PurchaseOrderPostProcessingStateSchema,
    }),
    PurchaseOrderProcessingErrorResultSchema,
]);

export const POProcessingStatusSchema = z.enum([
    "pending",
    "review",
    "error",
    "confirming",
    "confirmed",
    "approving",
    "declined",
    "cancelled",
    "erp-error",
]);

export const POProcessingSchema = z.object({
    id: z.number(),
    organizationId: OrganizationIdSchema,
    number: z.string(),
    status: POProcessingStatusSchema,
    result: z.nullable(POProcessingResultSchema),
    assignee: z.nullable(z.array(z.string())),
});

export const PartialConfirmationStrategySchema = z.enum(["ignoreDeliverySchedules", "splitPoItems", "proper"]);
export const ItemAcknowledgeNumberSchema = z.enum(["first", "last"]);
export const PurchaseOrderDeliveryTerms2StrategySchema = z.enum([
    "location",
    "locationSensitive",
    "meaning",
    "description",
    "ignore",
]);
export const DeliveryDateUpdateRuleSchema = z.object({
    type: z.enum(["always", "never"]),
});
export const StatDateUpdateRuleSchema = z.object({
    first: z.enum(["always", "never"]),
    subsequent: z.enum(["always", "before", "never"]),
});

export const PurchaseOrderSubmissionSettingsSchema = z.object({
    scheduleLines: z.optional(
        z.object({
            deliveryDate: DeliveryDateUpdateRuleSchema,
            statDate: StatDateUpdateRuleSchema,
        }),
    ),
});

export const ReclamationFeedbackSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("no"),
    }),
    z.object({
        type: z.literal("supplierEmail"),
        fallbackEmail: z.string(),
        overrideSupplierEmail: z.optional(z.string()),
        languages: z._default(z.array(LanguageSchema), ["DE", "EN"]),
    }),
    z.object({
        type: z.literal("comment"),
        language: LanguageSchema,
    }),
]);

export const PurchaseOrderInboxFolderSchema = z.enum(["review", "approval", "processed", "error", "cancelled"]);
export const PurchaseOrderInboxLabelSchema = z.enum([
    "review",
    "approval",
    "reclamation",
    "acceptedAuto",
    "acceptedTolerance",
    "acceptedEdited",
    "error",
    "cancelled",
]);

export const PurchaseOrderExtraItemMergingExtractionSchema = z.object({
    type: z.literal("mergeToUnitPrice"),
    mergingItemTypes: z.optional(z.array(PurchaseOrderExtraTypeSchema)),
    movingItemTypes: z.optional(z.array(PurchaseOrderExtraTypeSchema)),
    mergingHeaderTypes: z.optional(z.array(PurchaseOrderExtraTypeSchema)),
    headerTypeWhitelist: z.optional(z.array(PurchaseOrderExtraTypeSchema)),
});

export const PurchaseOrderWorkflowValidatorSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("condition"),
        condition: ConditionSchema,
        error: z.string(),
    }),
]);

export const PurchaseOrderWorkflowFieldCustomInstructionsSchema = z.union([
    z.string(),
    z.object({ promptId: z.string() }),
]);

export const PurchaseOrderWorkflowFieldExtractionSchema = z.object({
    instructions: z.optional(PurchaseOrderWorkflowFieldCustomInstructionsSchema),
    validators: z.optional(z.array(PurchaseOrderWorkflowValidatorSchema)),
});

export const PurchaseOrderWorkflowFieldComparisonSchema = z.object({
    instructions: z.optional(PurchaseOrderWorkflowFieldCustomInstructionsSchema),
});

export const PurchaseOrderWorkflowFieldsSchema = z.object({
    poNumber: z.object({
        extraction: z.optional(PurchaseOrderWorkflowFieldExtractionSchema),
    }),
    reference: z.object({
        extraction: z.optional(PurchaseOrderWorkflowFieldExtractionSchema),
        comparison: z.object({
            defaultValue: z.string(),
        }),
    }),
    paymentTerms: z.optional(
        z.object({
            extraction: z.optional(PurchaseOrderWorkflowFieldExtractionSchema),
            comparison: z.optional(PurchaseOrderWorkflowFieldComparisonSchema),
        }),
    ),
    deliveryTerms: z.optional(
        z.object({
            extraction: z.optional(PurchaseOrderWorkflowFieldExtractionSchema),
            comparison: z.optional(PurchaseOrderWorkflowFieldComparisonSchema),
            deliveryTerms2: PurchaseOrderDeliveryTerms2StrategySchema,
            useDefault: z.boolean(),
        }),
    ),
    deliveryAddress: z.optional(
        z.object({
            extraction: z.optional(PurchaseOrderWorkflowFieldExtractionSchema),
        }),
    ),
    aswoDeliveryState: z.optional(z.object({})),
    extraItems: z.optional(
        z.object({
            extraction: z.intersection(
                z.discriminatedUnion("type", [
                    PurchaseOrderExtraItemMergingExtractionSchema,
                    z.object({
                        type: z.literal("straight"),
                    }),
                ]),
                PurchaseOrderWorkflowFieldExtractionSchema,
            ),
            review: z.object({
                required: z.boolean(),
            }),
        }),
    ),
});

export const InboxFolderingRulesSchema = z.array(
    z.object({ condition: z.optional(ConditionSchema), path: z.string() }),
);

export const PurchaseOrderApprovalRolesSchema = z.record(
    z.string(),
    z.object({
        resolve: z.union([MasterDataQuerySchema, z.array(MasterDataQuerySchema)]),
        toEmail: z.optional(z.string()),
        authorization: z.optional(
            z.array(
                z.discriminatedUnion("type", [
                    z.object({
                        type: z.literal("query"),
                        if: z.optional(ConditionSchema),
                        query: z.union([MasterDataQuerySchema, z.array(MasterDataQuerySchema)]),
                        condition: ConditionSchema,
                    }),
                    z.object({
                        type: z.literal("condition"),
                        if: z.optional(ConditionSchema),
                        condition: z.optional(ConditionSchema),
                    }),
                ]),
            ),
        ),
    }),
);

export const PurchaseOrderWorkflowAutoSubmissionSettingsSchema = z.object({
    active: z.boolean(),
    asOrderedThresholds: z.optional(z.array(ThresholdSchema)),
    autoAcceptThresholds: z.optional(z.array(ThresholdSchema)),
    acceptMissing: z.boolean(),
    acceptNew: z.boolean(),
    acceptMasterPrice: z.boolean(),
});

export const PurchaseOrderWorkflowSupplierSettingsSchema = z.object({
    autoSubmission: z.optional(
        z.object({
            active: z.optional(z.boolean()),
            asOrderedThresholds: z.optional(z.array(ThresholdSchema)),
            autoAcceptThresholds: z.optional(z.array(ThresholdSchema)),
        }),
    ),
});

export const PurchaseOrderWorkflowSettingsSchema = z.object({
    fields: z._default(z.optional(PurchaseOrderWorkflowFieldsSchema), {
        poNumber: {},
        reference: {
            comparison: {
                defaultValue: "[{{po.head.number}}]",
            },
        },
        paymentTerms: {},
        deliveryTerms: {
            deliveryTerms2: "locationSensitive",
            useDefault: true,
        },
        deliveryAddress: {},
        extraItems: {
            extraction: {
                type: "mergeToUnitPrice",
            },
            review: {
                required: true,
            },
        },
    }),
    suppliers: z.optional(
        z.object({
            source: z.discriminatedUnion("type", [
                z.object({
                    type: z.literal("no"),
                }),
                z.object({
                    type: z.literal("entitySource"),
                }),
                z.object({
                    type: z.literal("sapBPODataV2"),
                    connection: IntegrationConnectionSchema,
                    auth: z.optional(IntegrationAuthSchema),
                }),
            ]),
        }),
    ),
    reclamation: z.optional(
        z.object({
            update: z.discriminatedUnion("type", [
                z.object({
                    type: z.literal("discard"),
                }),
                z.object({
                    type: z.literal("partial"),
                }),
            ]),
            feedback: ReclamationFeedbackSchema,
        }),
    ),
    autoSubmission: z.optional(PurchaseOrderWorkflowAutoSubmissionSettingsSchema),
    review: z.optional(
        z.object({
            recordConfirmations: z.optional(
                z.object({
                    confirmationNumberPattern: z.optional(z.string()),
                    confirmConfirmationLines: z.optional(z.boolean()),
                }),
            ),
        }),
    ),
    approval: z.optional(
        z.object({
            roles: PurchaseOrderApprovalRolesSchema,
            thresholds: z._default(z.array(ApprovalThresholdSchema), []),
            firstReminderAfter: z.optional(z.number()),
            nextReminderAfter: z.optional(z.number()),
            confirmBeforeApproval: z.optional(
                z.union([
                    z.boolean(),
                    z.object({
                        confirmationNumberPattern: z.optional(z.string()),
                        confirmConfirmationLines: z.optional(z.boolean()),
                    }),
                ]),
            ),
            confirmIntermediateApproval: z.optional(z.boolean()),
            auto: z.optional(z.boolean()),
            sendEmail: z._default(z.optional(z.boolean()), true),
        }),
    ),
    shipmentDateStrategy: z._default(
        z.discriminatedUnion("type", [
            z.object({
                type: z.literal("addDays"),
                amount: z.number(),
            }),
            z.object({
                type: z.literal("ignore"),
            }),
            z.object({
                type: z.literal("addDaysBySupplier"),
                defaultAdditionalDeliveryDays: z.number(),
                defaultAdditionalShipmentDays: z.number(),
            }),
        ]),
        { type: "addDays", amount: 2 },
    ),
    weekNumberAbsoluteDay: z._default(z.number(), 4),
    defaultConfirmationControlKey: z.optional(z.string()),
    inboxManagement: z.optional(
        z.object({
            folders: z.optional(
                z.array(
                    z.discriminatedUnion("type", [
                        z.object({
                            inboxId: InboxIdSchema,
                            type: z.literal("default"),
                        }),
                        z.object({
                            inboxId: InboxIdSchema,
                            type: z.literal("rules"),
                            rules: z.partialRecord(PurchaseOrderInboxFolderSchema, InboxFolderingRulesSchema),
                        }),
                    ]),
                ),
            ),
            labels: z.optional(
                z.array(
                    z.discriminatedUnion("type", [
                        z.object({
                            type: z.literal("default"),
                            inboxId: InboxIdSchema,
                        }),
                        z.object({
                            type: z.literal("rules"),
                            inboxId: InboxIdSchema,
                            sourceFolderIds: z.optional(z.array(z.string())),
                            rules: z.union([
                                z.partialRecord(
                                    PurchaseOrderInboxLabelSchema,
                                    z.union([
                                        z.object({
                                            entitySource: z.string(),
                                            keyTemplate: z.string(),
                                            labelTemplate: z.string(),
                                        }),
                                        z.object({ template: z.string() }),
                                    ]),
                                ),
                            ]),
                        }),
                    ]),
                ),
            ),
        }),
    ),
    readOnly: z.optional(z.boolean()),
    comparisonWorkflow: z._default(z.enum(["v1", "v2"]), "v1"),
    minCurrencyPrecision: z._default(z.optional(z.number()), 2),
    minQuantityPrecision: z._default(z.optional(z.number()), 0),
    partialConfirmationStrategy: z._default(PartialConfirmationStrategySchema, "proper"),
    itemAcknowledgeNumber: z._default(ItemAcknowledgeNumberSchema, "first"),
    submission: z.optional(PurchaseOrderSubmissionSettingsSchema),
});

export const PushedAttachment = z.object({
    organizationId: OrganizationIdSchema,
    poNumber: z.string(),
    attachmentId: z.union([AttachmentIdSchema, MessageIdSchema]),
});

export const ApprovalTaskSchema = z.object({
    id: ApprovalTaskIdSchema,
    email: z.string(),
    poProcessingId: z.number(),
    createdAt: z.string(),
    lastReminderSentAt: z.nullable(z.string()),
    active: z.boolean(),
});

export const UifyNotificationAction = z.object({
    actionId: z.string(),
    apiKey: z.string(),
    env: z.optional(z.string()),
});

export const CaseTagIdSchema = EntityIdSchema("ctg");
export const CaseTagSchema = z.object({
    id: CaseTagIdSchema,
    slug: z.string(),
    labelEn: z.string(),
    labelDe: z.string(),
    color: z.string(),
    organizationId: z.nullable(z.string()),
});

export const CaseStatusSchema = z.enum([
    "NONE",
    "PROCESSING",
    "REVIEW",
    "APPROVAL",
    "REJECTED",
    "ACCEPTED",
    "ACCEPTED_EDITED",
    "ACCEPTED_AUTO",
    "ACCEPTED_TOLERANCE",
    "SUBMITTED",
    "SUBMITTED_AUTO",
    "ERROR",
    "CANCELLED",
    "ERP_ERROR",
]);

export const XmlArrayDefinitionSchema = z.object({
    array: z.string(),
    each: z.object({
        item: z.lazy(() => XmlNodeDefinitionSchema),
    }),
});

const XmlAttrDateDefinitionSchema = z.object({
    type: z.literal("date"),
    value: z.string(),
    sourceFormat: z.string(),
    targetFormat: z.string(),
});

const XmlAttrArrayDefinitionSchema = z.object({
    array: z.string(),
    one: z.object({
        if: ConditionSchema,
        get value() {
            return XmlAttrDefinitionSchema;
        },
    }),
});

export const XmlAttrDefinitionSchema = z
    .union([z.string(), XmlAttrDateDefinitionSchema, XmlAttrArrayDefinitionSchema])
    .register(z.globalRegistry, { title: "XmlAttrDefinition" });

export const XmlNodeDefinitionSchema = z.object({
    name: z.string(),
    if: z.optional(ConditionSchema),
    attrs: z.optional(z.record(z.string(), XmlAttrDefinitionSchema)),
    get children() {
        return z.optional(z.union([z.array(XmlNodeDefinitionSchema), XmlArrayDefinitionSchema, z.string()]));
    },
});
XmlNodeDefinitionSchema.register(z.globalRegistry, { title: "XmlNodeDefinition" });

export const ClassifyThreadClassSchema = z.enum(["orderConfirmation", "contractOrderConfirmation", "other"]);
export const ClassifyThreadRuleTypeSchema = z.union([ClassifyThreadClassSchema, z.literal("unclassified")]);

export const ClassifyThreadInboxRule = z.union([
    z.object({
        inboxId: InboxIdSchema,
        parentFolderId: z.string(),
        name: z.string(),
        sourceFolderIds: z.optional(z.array(z.string())),
    }),
    z.object({
        inboxId: InboxIdSchema,
        rules: InboxFolderingRulesSchema,
    }),
]);

export const ClassifyThreadWorkflowSettingsSchema = z.object({
    inboxRules: z.optional(
        z.partialRecord(
            ClassifyThreadRuleTypeSchema,
            z.object({
                folders: z.optional(z.array(ClassifyThreadInboxRule)),
            }),
        ),
    ),
});

export const DispatchWorkflowSettingsSchema = z.object({
    rules: z.array(
        z.object({
            if: z.optional(ConditionSchema),
            workflow: z.object({
                type: z.string(),
                args: z.optional(z.string()),
                runType: z.enum(["start", "execute"]),
            }),
            ui: z.optional(
                z.object({
                    name: z.enum(["UNIVERSAL"]),
                }),
            ),
        }),
    ),
});
